/**
 * FileExplorer - Componente Explorer stile Windows
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  ChevronRight,
  Home,
  ArrowUp,
  RefreshCw,
  Search,
  FolderPlus,
  Upload,
  Trash2,
  Eye,
  MoreVertical,
  AlertCircle,
  Loader2,
  X,
  ExternalLink,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { FileIcon, isPreviewable } from './FileIcon';
import { FilePreview } from './FilePreview';
import { useFileSystem, FileSystemEntry, savePracticeHandle, resolveHandleFromBasePath } from '@/hooks/useFileSystem';
import { useElectronFileSystem, isElectronEnvironment } from '@/hooks/useElectronFileSystem';
import { usePracticeBasePath } from '@/hooks/usePracticeBasePath';
import { useClientDocumentsConfig } from '@/hooks/useClientDocumentsConfig';
import { apiClient } from '@/utils/api';
import { toast } from 'sonner';
import {
  normalizeDocumentPath,
  normalizePickerResultPath,
  DEFAULT_NAS_PICKER_ROOT,
  resolveDocumentPathWithNasRoot,
} from '@/utils/normalizeDocumentPath';
import { countFilesRecursiveElectron } from '@/utils/electronFolderScan';
import { practiceService } from '@/services/practiceService';

interface FileExplorerProps {
  practiceId: string;
  basePath?: string; // Percorso base salvato (es. "Y:\Area ICT")
  onPathConfigured?: (path: string) => void | Promise<void>;
  onFileCountChange?: (count: number) => void; // Callback per aggiornare il conteggio file (ricorsivo in Electron)
  /** Ultimo conteggio salvato sul server (evita PATCH inutili) */
  serverFolderFileCount?: number | null;
  /** Dopo PATCH riuscito */
  onFolderCountPersisted?: (count: number) => void;
  /** Incrementare da parent per forzare scansione + salvataggio (bottone Conta) */
  recountNonce?: number;
  className?: string;
}

// Formatta dimensione file
function formatFileSize(bytes?: number): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

// Formatta data
function formatDate(timestamp?: number): string {
  if (!timestamp) return '-';
  return new Date(timestamp).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function FileExplorer({
  practiceId,
  basePath,
  onPathConfigured,
  onFileCountChange,
  serverFolderFileCount,
  onFolderCountPersisted,
  recountNonce = 0,
  className,
}: FileExplorerProps) {
  const basePathHook = usePracticeBasePath();
  const { documentsNasRoot } = useClientDocumentsConfig();

  /** Path reale sul disco: unisce root NAS se il DB ha solo il nome cartella o path tipo /4748_... */
  const resolvedFsPath = useMemo(() => {
    if (basePath == null || basePath === '') return undefined;
    const r = resolveDocumentPathWithNasRoot(basePath, documentsNasRoot || DEFAULT_NAS_PICKER_ROOT);
    return r || undefined;
  }, [basePath, documentsNasRoot]);

  const inElectron = isElectronEnvironment();
  const browserFS = useFileSystem(practiceId);
  const electronFS = useElectronFileSystem(resolvedFsPath);
  const fileSystem = inElectron ? electronFS : browserFS;
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<FileSystemEntry[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<FileSystemEntry | null>(null);
  const [previewEntry, setPreviewEntry] = useState<FileSystemEntry | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [showFileInfoDialog, setShowFileInfoDialog] = useState(false);
  const [fileInfoEntry, setFileInfoEntry] = useState<FileSystemEntry | null>(null);
  const [showPathInputDialog, setShowPathInputDialog] = useState(false);
  const [pathInputValue, setPathInputValue] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [autoLinkAttempted, setAutoLinkAttempted] = useState(false);

  // Reset autoLinkAttempted quando cambia la pratica
  useEffect(() => {
    setAutoLinkAttempted(false);
  }, [practiceId]);

  // Auto-link (solo browser): se non c'è handle pratica ma il base path è autorizzato, deriva automaticamente.
  useEffect(() => {
    if (inElectron) return;
    if (autoLinkAttempted || fileSystem.hasPermission) return;
    if (!fileSystem.initialLoadDone) return;
    if (!resolvedFsPath || !basePathHook.baseHandle) return;

    setAutoLinkAttempted(true);
    (async () => {
      try {
        const handle = await resolveHandleFromBasePath(practiceId, resolvedFsPath);
        if (handle) {
          await fileSystem.reloadFromDB();
        }
      } catch {
        // silently fail, user can manually authorize
      }
    })();
  }, [autoLinkAttempted, fileSystem.hasPermission, fileSystem.initialLoadDone, resolvedFsPath, basePathHook.baseHandle, practiceId, fileSystem.reloadFromDB]);

  const lastPersistedRef = useRef<number | undefined>(undefined);
  const onFileCountChangeRef = useRef(onFileCountChange);
  const onFolderCountPersistedRef = useRef(onFolderCountPersisted);
  onFileCountChangeRef.current = onFileCountChange;
  onFolderCountPersistedRef.current = onFolderCountPersisted;

  useEffect(() => {
    lastPersistedRef.current = serverFolderFileCount ?? undefined;
  }, [serverFolderFileCount, practiceId]);

  // Electron: conteggio ricorsivo + sync server (debounce)
  useEffect(() => {
    if (!inElectron || !resolvedFsPath || !window.electronFS) return;
    if (!fileSystem.initialLoadDone) return;

    const run = () => {
      const total = countFilesRecursiveElectron(resolvedFsPath);
      onFileCountChangeRef.current?.(total);
      if (lastPersistedRef.current === total) return;
      practiceService
        .updateFolderFileCount(practiceId, total)
        .then(() => {
          lastPersistedRef.current = total;
          onFolderCountPersistedRef.current?.(total);
        })
        .catch(() => {
          /* silenzioso: rete o permessi */
        });
    };

    const t = window.setTimeout(run, 700);
    return () => window.clearTimeout(t);
  }, [inElectron, resolvedFsPath, practiceId, fileSystem.initialLoadDone]);

  // Browser: conteggio solo livello corrente (nessun sync NAS lato server)
  useEffect(() => {
    if (inElectron) return;
    if (onFileCountChange && fileSystem.entries.length > 0) {
      const fileCount = fileSystem.entries.filter((e) => e.kind === 'file').length;
      onFileCountChange(fileCount);
    }
  }, [inElectron, fileSystem.entries, onFileCountChange]);

  // Bottone Conta: forza scansione e salvataggio
  useEffect(() => {
    if (!recountNonce) return;
    if (!inElectron || !resolvedFsPath || !window.electronFS) return;
    const total = countFilesRecursiveElectron(resolvedFsPath);
    onFileCountChangeRef.current?.(total);
    practiceService
      .updateFolderFileCount(practiceId, total)
      .then(() => {
        lastPersistedRef.current = total;
        onFolderCountPersistedRef.current?.(total);
        toast.success(`Conteggio aggiornato: ${total} file`);
      })
      .catch(() => toast.error('Impossibile salvare il conteggio'));
  }, [recountNonce, inElectron, resolvedFsPath, practiceId]);

  // Funzione per registrare operazioni sui documenti nella timeline
  const logDocumentOperation = async (operation: string, fileName: string, filePath?: string) => {
    try {
      await apiClient.post(`/practices/${practiceId}/document-log`, {
        operation,
        fileName,
        filePath,
      });
    } catch (error) {
      console.error('Errore nel logging operazione documento:', error);
    }
  };

  // Gestisce la selezione della directory — in Electron: dialog nativo sul NAS; in browser: input + picker
  const handleSelectDirectory = async () => {
    if (inElectron && window.electronDialog?.pickDirectory) {
      const defaultPick =
        normalizeDocumentPath(documentsNasRoot || '') || DEFAULT_NAS_PICKER_ROOT;
      const picked = await window.electronDialog.pickDirectory(defaultPick);
      if (picked && onPathConfigured) {
        await Promise.resolve(onPathConfigured(normalizePickerResultPath(picked)));
      }
      return;
    }
    setPathInputValue('');
    setShowPathInputDialog(true);
  };

  // Conferma la selezione della directory con il percorso inserito
  const handleConfirmDirectorySelection = async () => {
    if (!pathInputValue.trim()) {
      toast.error('Inserisci il percorso completo della cartella');
      return;
    }
    
    // Ora seleziona la cartella
    const folderName = await fileSystem.selectDirectory();
    if (folderName && onPathConfigured) {
      onPathConfigured(normalizeDocumentPath(pathInputValue.trim()));
    }
    setShowPathInputDialog(false);
    setPathInputValue('');
  };

  // Costruisce il percorso completo del file (usando il basePath salvato)
  const getFullPath = (entry: FileSystemEntry) => {
    let fullPath = resolvedFsPath || '';
    
    // Assicurati che il basePath termini senza backslash
    if (fullPath.endsWith('\\') || fullPath.endsWith('/')) {
      fullPath = fullPath.slice(0, -1);
    }
    
    // Se l'entry ha un path (es. da risultati ricerca), usalo direttamente
    // Altrimenti usa il currentPath + nome
    if (entry.path && entry.path !== entry.name) {
      // L'entry ha un path completo dalla ricerca (relativo alla root)
      const entryPath = entry.path.replace(/\//g, '\\');
      fullPath = fullPath ? `${fullPath}\\${entryPath}` : entryPath;
    } else {
      // Navigazione normale: usa currentPath + nome
      const relativePath = fileSystem.currentPath.join('\\');
      if (relativePath) {
        fullPath = fullPath ? `${fullPath}\\${relativePath}` : relativePath;
      }
      fullPath = fullPath ? `${fullPath}\\${entry.name}` : entry.name;
    }
    
    return fullPath;
  };

  const openWithProtocol = (entry: FileSystemEntry) => {
    // In Electron: apri direttamente con l'app predefinita del sistema
    if (inElectron && window.electronFS) {
      const fullPath = getFullPath(entry);
      window.electronFS.openPath(fullPath);
      return;
    }

    const fullPath = getFullPath(entry);
    const encodedPath = encodeURIComponent(fullPath);
    const protocolUrl = `studiogorfile://${encodedPath}`;

    const link = document.createElement('a');
    link.href = protocolUrl;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.info('Apertura file in corso...', {
      description: 'Serve il client desktop Studio Gori (MSI). Se non si apre, installalo dalla pagina Download.',
      action: {
        label: 'Download',
        onClick: () => window.open('/downloads/', '_blank'),
      },
    });
  };

  // Gestisce il click su un entry
  const handleEntryClick = async (entry: FileSystemEntry) => {
    if (entry.kind === 'directory') {
      const newPath = entry.path.split('/');
      fileSystem.navigateTo(newPath);
      setSearchResults(null);
      setSearchQuery('');
    } else if (isPreviewable(entry.name)) {
      // File previewable: mostra anteprima interna
      const blob = await fileSystem.openFile(entry);
      if (blob) {
        setPreviewEntry(entry);
        setPreviewBlob(blob);
      }
    } else {
      // File non previewable (Word, Excel, ZIP, ecc.): prova ad aprire con helper
      // Se l'helper non è installato, il browser mostrerà un messaggio
      openWithProtocol(entry);
    }
  };

  // Ricerca file
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    setIsSearching(true);
    const results = await fileSystem.searchFiles(searchQuery);
    setSearchResults(results);
    setIsSearching(false);
  }, [searchQuery, fileSystem]);

  // Crea nuova cartella
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    const folderName = newFolderName.trim();
    const currentPath = fileSystem.currentPath.join('/');
    await fileSystem.createFolder(folderName);
    setNewFolderName('');
    setShowNewFolderDialog(false);
    // Log nella timeline
    await logDocumentOperation('FOLDER_CREATED', folderName, currentPath || undefined);
    toast.success(`Cartella "${folderName}" creata`);
  };

  // Elimina entry
  const handleDelete = async () => {
    if (!entryToDelete) return;
    const { name, kind, path } = entryToDelete;
    const parentPath = path.split('/').slice(0, -1).join('/');
    await fileSystem.deleteEntry(entryToDelete);
    // Log nella timeline
    const operation = kind === 'directory' ? 'FOLDER_DELETED' : 'FILE_DELETED';
    await logDocumentOperation(operation, name, parentPath || undefined);
    toast.success(kind === 'directory' 
      ? `Cartella "${name}" e tutto il suo contenuto eliminati definitivamente`
      : `File "${name}" eliminato definitivamente`);
    setEntryToDelete(null);
    setShowDeleteDialog(false);
  };

  // Upload file
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const currentPath = fileSystem.currentPath.join('/');
    for (const file of Array.from(files)) {
      await fileSystem.saveFile(file);
      // Log nella timeline
      await logDocumentOperation('FILE_CREATED', file.name, currentPath || undefined);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    if (files.length > 0) {
      toast.success(files.length === 1 
        ? `File "${files[0].name}" caricato`
        : `${files.length} file caricati`);
    }
  };

  // Gestisce drag and drop
  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const currentPath = fileSystem.currentPath.join('/');
    const items = event.dataTransfer.items;
    let uploadedCount = 0;
    
    for (const item of Array.from(items)) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          await fileSystem.saveFile(file);
          // Log nella timeline
          await logDocumentOperation('FILE_CREATED', file.name, currentPath || undefined);
          uploadedCount++;
        }
      }
    }
    
    if (uploadedCount > 0) {
      toast.success(uploadedCount === 1 
        ? 'File caricato'
        : `${uploadedCount} file caricati`);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  // Browser non supportato (solo web, Electron usa il preload bridge)
  if (!fileSystem.isSupported && !inElectron) {
    return (
      <div className={cn('flex flex-col items-center justify-center p-8 text-center', className)}>
        <AlertCircle className="h-16 w-16 text-yellow-500 mb-4" />
        <h3 className="text-lg font-semibold mb-2">Browser non supportato</h3>
        <p className="text-muted-foreground max-w-md">
          La gestione documenti richiede un browser che supporti la File System Access API.
          Per favore usa <strong>Google Chrome</strong>, <strong>Microsoft Edge</strong> o <strong>Opera</strong>.
        </p>
      </div>
    );
  }

  // Nessuna cartella selezionata o permesso scaduto
  if (!fileSystem.hasPermission) {
    // Electron: percorso NAS non raggiungibile — mostra errore semplice
    if (inElectron) {
      return (
        <div className={cn('flex flex-col items-center justify-center p-8 text-center', className)}>
          <AlertCircle className="h-16 w-16 text-yellow-500 mb-4" />
          {resolvedFsPath ? (
            <>
              <h3 className="text-lg font-semibold mb-2">Cartella non raggiungibile</h3>
              <p className="text-muted-foreground max-w-md mb-2">
                Il percorso <strong className="font-mono text-xs break-all">{resolvedFsPath}</strong> non è
                accessibile da questo computer. Verifica che il NAS sia connesso e il percorso sia corretto.
              </p>
              {fileSystem.error && (
                <p className="text-red-500 text-sm mt-2">{fileSystem.error}</p>
              )}
              <div className="flex flex-wrap gap-2 justify-center mt-4">
                <Button onClick={() => fileSystem.reloadFromDB()}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Riprova
                </Button>
                <Button variant="outline" onClick={handleSelectDirectory}>
                  Cambia cartella
                </Button>
              </div>
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold mb-2">Nessuna cartella configurata</h3>
              <p className="text-muted-foreground max-w-md mb-2">
                Questa pratica non ha un percorso documenti configurato. Scegli la cartella sul NAS (si apre in{' '}
                <span className="font-mono text-xs">\\nas_gori\dati_nas\Public\Volume_1</span>) oppure configuralo dalla
                scheda Info.
              </p>
              <Button className="mt-4" onClick={handleSelectDirectory}>
                <FolderPlus className="h-4 w-4 mr-2" />
                Seleziona cartella sul NAS
              </Button>
            </>
          )}
        </div>
      );
    }

    const handleRequestPermission = async () => {
      const granted = await fileSystem.requestPermission();
      if (!granted) {
        toast.error('Permesso negato. Seleziona di nuovo la cartella.');
      }
    };

    // Calcola il nome della sotto-cartella pratica e il percorso padre dal basePath
    const basePathSegments = resolvedFsPath ? resolvedFsPath.split(/[/\\]/).filter(Boolean) : [];
    const practiceFolderName = basePathSegments.length > 0 ? basePathSegments[basePathSegments.length - 1] : '';
    const parentPathDisplay =
      basePathSegments.length > 1
        ? basePathSegments.slice(0, -1).join('\\')
        : documentsNasRoot || '';

    const linkPracticeFolderFromBase = async (baseHandle: FileSystemDirectoryHandle) => {
      if (!resolvedFsPath || !practiceFolderName) return false;
      try {
        if (practiceFolderName === baseHandle.name) {
          // L'handle È già la cartella pratica
          await savePracticeHandle(practiceId, baseHandle, resolvedFsPath);
        } else {
          const practiceFolder = await (baseHandle as any).getDirectoryHandle(practiceFolderName, { create: false });
          await savePracticeHandle(practiceId, practiceFolder, resolvedFsPath);
        }
        toast.success('Cartella pratica collegata');
        await fileSystem.reloadFromDB();
        return true;
      } catch (err: any) {
        if (err.name === 'NotFoundError') {
          toast.error(`Sotto-cartella "${practiceFolderName}" non trovata. Assicurati di aver selezionato la cartella padre corretta.`);
        } else {
          toast.error(`Errore: ${err.message}`);
        }
        return false;
      }
    };

    const handleUseBasePath = async () => {
      let handle = basePathHook.baseHandle;
      if (!handle && basePathHook.hasSavedPath) {
        const granted = await basePathHook.requestPermissionAndReload();
        if (!granted) {
          toast.error('Permesso negato sul percorso base.');
          return;
        }
        await basePathHook.loadBasePath();
        handle = basePathHook.baseHandle;
      }
      if (!handle) {
        toast.error('Percorso base non disponibile.');
        return;
      }
      if (resolvedFsPath) {
        await linkPracticeFolderFromBase(handle);
      } else {
        toast.info('Seleziona la cartella della pratica dal percorso base.');
        const folderName = await fileSystem.selectDirectory();
        if (folderName && onPathConfigured) {
          onPathConfigured(`${handle.name}/${folderName}`);
        }
      }
    };

    const handleSelectBasePathForNewPC = async () => {
      if (!window.showDirectoryPicker) return;
      try {
        const handle = (await window.showDirectoryPicker({
          id: 'practice-base-picker',
          mode: 'readwrite',
        })) as any;

        if (!handle) return;

        if (practiceFolderName && handle.name === practiceFolderName) {
          // L'utente ha selezionato la cartella della PRATICA, non il padre.
          // Salva solo come handle di questa pratica, NON come base path.
          await savePracticeHandle(practiceId, handle, resolvedFsPath!);
          toast.success('Cartella pratica collegata');
          toast.info('Per collegare automaticamente le altre pratiche, la prossima volta seleziona la cartella padre.');
          await fileSystem.reloadFromDB();
          return;
        }

        // L'utente ha selezionato la cartella padre (corretto).
        // Verifica che contenga la sotto-cartella della pratica.
        try {
          await handle.getDirectoryHandle(practiceFolderName, { create: false });
        } catch {
          toast.error(`La cartella selezionata non contiene "${practiceFolderName}". Seleziona la cartella che contiene le sotto-cartelle delle pratiche.`);
          return;
        }

        // Salva come base path (per tutte le pratiche su questo PC)
        await basePathHook.selectBasePathWithHandle(handle);
        await linkPracticeFolderFromBase(handle);
      } catch (err: any) {
        if (err.name === 'AbortError') return;
        toast.error(`Errore nella selezione: ${err.message}`);
      }
    };

    return (
      <>
        <div className={cn('flex flex-col items-center justify-center p-8 text-center', className)}>
          <FolderPlus className="h-16 w-16 text-muted-foreground mb-4" />
          {fileSystem.savedHandleExists ? (
            <>
              <h3 className="text-lg font-semibold mb-2">Autorizza accesso alla cartella</h3>
              <p className="text-muted-foreground max-w-md mb-4">
                La cartella documenti è già configurata ma il permesso di accesso è scaduto.
                Clicca per autorizzare di nuovo l'accesso.
              </p>
              <div className="flex gap-2">
                <Button onClick={handleRequestPermission} disabled={fileSystem.isLoading}>
                  {fileSystem.isLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FolderPlus className="h-4 w-4 mr-2" />
                  )}
                  Autorizza Accesso
                </Button>
                <Button variant="outline" onClick={handleSelectDirectory} disabled={fileSystem.isLoading}>
                  Cambia Cartella
                </Button>
              </div>
            </>
          ) : basePathHook.hasSavedPath ? (
            <>
              <h3 className="text-lg font-semibold mb-2">Collega cartella documenti</h3>
              <p className="text-muted-foreground max-w-md mb-4">
                {resolvedFsPath
                  ? 'La cartella della pratica esiste nel percorso base. Clicca per autorizzare l\'accesso.'
                  : 'Autorizza l\'accesso al percorso base per collegare la cartella documenti.'}
              </p>
              <div className="flex gap-2 flex-wrap justify-center">
                <Button onClick={handleUseBasePath} disabled={fileSystem.isLoading}>
                  {fileSystem.isLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FolderPlus className="h-4 w-4 mr-2" />
                  )}
                  Autorizza Percorso Base
                </Button>
                <Button variant="outline" onClick={handleSelectDirectory} disabled={fileSystem.isLoading}>
                  Seleziona Altra Cartella
                </Button>
              </div>
            </>
          ) : resolvedFsPath ? (
            <>
              <h3 className="text-lg font-semibold mb-2">Autorizza accesso documenti</h3>
              <p className="text-muted-foreground max-w-md mb-4">
                La cartella documenti è già configurata sul server. Per questo PC, autorizza la{' '}
                <strong>cartella padre</strong> sul NAS (di solito coincide con l&apos;ultimo segmento del percorso in
                Impostazioni, es. <strong>Volume_1</strong>). Una sola volta per computer.
              </p>
              {parentPathDisplay && (
                <div className="mb-4 max-w-md text-left">
                  <p className="text-xs text-muted-foreground mb-1">Cartella da selezionare:</p>
                  <p className="text-sm font-mono bg-green-50 border border-green-200 text-green-800 p-2 rounded break-all">
                    {parentPathDisplay}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Sotto-cartella pratica: <span className="font-mono">{practiceFolderName}</span>
                  </p>
                </div>
              )}
              <div className="flex gap-2 flex-wrap justify-center">
                <Button onClick={handleSelectBasePathForNewPC} disabled={fileSystem.isLoading}>
                  {fileSystem.isLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FolderPlus className="h-4 w-4 mr-2" />
                  )}
                  Seleziona Cartella Padre
                </Button>
              </div>
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold mb-2">Configura cartella documenti</h3>
              <p className="text-muted-foreground max-w-md mb-4">
                Seleziona la cartella locale o di rete che contiene i documenti di questa pratica.
                Il browser ti chiederà il permesso di accedere alla cartella.
              </p>
              <Button onClick={handleSelectDirectory} disabled={fileSystem.isLoading}>
                {fileSystem.isLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FolderPlus className="h-4 w-4 mr-2" />
                )}
                Seleziona Cartella
              </Button>
            </>
          )}
          {fileSystem.error && (
            <p className="text-red-500 text-sm mt-2">{fileSystem.error}</p>
          )}
        </div>
        
        {/* Path Input Dialog - necessario anche qui per l'early return */}
        <Dialog open={showPathInputDialog} onOpenChange={setShowPathInputDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Configura cartella documenti</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Inserisci il percorso UNC completo della cartella pratica (allineato al percorso NAS in Impostazioni
                {documentsNasRoot ? `: ${documentsNasRoot}` : ''}).
              </p>
              <div className="space-y-2">
                <label className="text-sm font-medium">Percorso cartella:</label>
                <Input
                  placeholder="Es: Y:\Area ICT\Clienti\NomePratica"
                  value={pathInputValue}
                  onChange={(e) => setPathInputValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleConfirmDirectorySelection()}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  💡 Copia il percorso da Esplora File (barra degli indirizzi) per essere sicuro
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPathInputDialog(false)}>
                Annulla
              </Button>
              <Button onClick={handleConfirmDirectorySelection} disabled={!pathInputValue.trim()}>
                Seleziona Cartella
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  const displayEntries = searchResults ?? fileSystem.entries;

  return (
    <div
      className={cn('flex flex-col h-full', className)}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      {/* Toolbar */}
      <div className="flex items-center gap-2 p-2 border-b bg-muted/30">
        <Button
          variant="ghost"
          size="icon"
          onClick={fileSystem.navigateToRoot}
          title="Vai alla root"
        >
          <Home className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={fileSystem.navigateUp}
          disabled={fileSystem.currentPath.length === 0}
          title="Cartella superiore"
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={fileSystem.refresh}
          disabled={fileSystem.isLoading}
          title="Aggiorna"
        >
          <RefreshCw className={cn('h-4 w-4', fileSystem.isLoading && 'animate-spin')} />
        </Button>

        {/* Breadcrumb */}
        <div className="flex items-center gap-1 flex-1 min-w-0 px-2">
          <button
            onClick={fileSystem.navigateToRoot}
            className="text-sm font-medium hover:underline truncate"
          >
            {inElectron
              ? (resolvedFsPath?.split(/[/\\]/).filter(Boolean).pop() || 'Root')
              : (fileSystem.rootHandle?.name || 'Root')}
          </button>
          {fileSystem.currentPath.map((segment, index) => (
            <div key={index} className="flex items-center">
              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <button
                onClick={() => fileSystem.navigateTo(fileSystem.currentPath.slice(0, index + 1))}
                className="text-sm hover:underline truncate"
              >
                {segment}
              </button>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="flex items-center gap-1">
          <Input
            placeholder="Cerca file..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="w-48 h-8"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setSearchQuery('');
                setSearchResults(null);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSearch}
            disabled={isSearching}
          >
            {isSearching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Actions */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowNewFolderDialog(true)}
          title="Nuova cartella"
        >
          <FolderPlus className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => fileInputRef.current?.click()}
          title="Carica file"
        >
          <Upload className="h-4 w-4" />
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileUpload}
        />

        <Button
          variant="outline"
          size="sm"
          onClick={handleSelectDirectory}
          title={inElectron ? 'Si apre in \\nas_gori\\dati_nas\\Public\\Volume_1' : undefined}
        >
          Cambia Cartella
        </Button>
      </div>

      {/* Error */}
      {fileSystem.error && (
        <div className="flex items-center gap-2 p-2 bg-red-50 text-red-700 text-sm border-b">
          <AlertCircle className="h-4 w-4" />
          {fileSystem.error}
        </div>
      )}

      {/* Search results header */}
      {searchResults && (
        <div className="flex items-center gap-2 p-2 bg-blue-50 text-blue-700 text-sm border-b">
          <Search className="h-4 w-4" />
          {searchResults.length} risultati per "{searchQuery}"
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearchQuery('');
              setSearchResults(null);
            }}
          >
            Annulla ricerca
          </Button>
        </div>
      )}

      {/* File list */}
      <div className="flex-1 overflow-auto">
        {fileSystem.isLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : displayEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
            <p>Cartella vuota</p>
            <p className="text-sm">Trascina qui i file o usa il pulsante carica</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-muted/50 sticky top-0">
              <tr className="text-left text-sm text-muted-foreground">
                <th className="p-2 font-medium">Nome</th>
                <th className="p-2 font-medium w-24">Dimensione</th>
                <th className="p-2 font-medium w-40">Data modifica</th>
                <th className="p-2 font-medium w-16"></th>
              </tr>
            </thead>
            <tbody>
              {displayEntries.map((entry) => (
                <tr
                  key={entry.path}
                  className="border-b hover:bg-muted/30 cursor-pointer"
                  onDoubleClick={() => handleEntryClick(entry)}
                >
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      <FileIcon name={entry.name} kind={entry.kind} />
                      <span
                        className="truncate"
                        title={searchResults ? entry.path : entry.name}
                      >
                        {searchResults ? entry.path : entry.name}
                      </span>
                    </div>
                  </td>
                  <td className="p-2 text-sm text-muted-foreground">
                    {entry.kind === 'file' ? formatFileSize(entry.size) : '-'}
                  </td>
                  <td className="p-2 text-sm text-muted-foreground">
                    {formatDate(entry.lastModified)}
                  </td>
                  <td className="p-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {entry.kind === 'directory' ? (
                          <DropdownMenuItem onClick={() => handleEntryClick(entry)}>
                            <FolderPlus className="h-4 w-4 mr-2" />
                            Apri cartella
                          </DropdownMenuItem>
                        ) : (
                          <>
                            {isPreviewable(entry.name) ? (
                              <DropdownMenuItem onClick={() => handleEntryClick(entry)}>
                                <Eye className="h-4 w-4 mr-2" />
                                Anteprima
                              </DropdownMenuItem>
                            ) : (
                              <>
                                <DropdownMenuItem onClick={() => openWithProtocol(entry)}>
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  Apri con programma
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                                  setFileInfoEntry(entry);
                                  setShowFileInfoDialog(true);
                                }}>
                                  <Info className="h-4 w-4 mr-2" />
                                  Info percorso
                                </DropdownMenuItem>
                              </>
                            )}
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => {
                            setEntryToDelete(entry);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Elimina
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* New Folder Dialog */}
      <Dialog open={showNewFolderDialog} onOpenChange={setShowNewFolderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuova Cartella</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Nome cartella"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewFolderDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreateFolder} disabled={!newFolderName.trim()}>
              Crea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Eliminazione definitiva
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              {entryToDelete?.kind === 'directory' ? (
                <>
                  <p>
                    Stai per eliminare <strong>definitivamente</strong> la cartella:
                  </p>
                  <p className="font-mono bg-muted p-2 rounded text-sm">
                    📁 {entryToDelete?.name}
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700">
                    <p className="font-semibold">⚠️ Attenzione!</p>
                    <p className="text-sm mt-1">
                      Tutti i file e le sottocartelle contenuti verranno eliminati permanentemente 
                      dal tuo disco. Questa operazione non può essere annullata.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p>
                    Stai per eliminare <strong>definitivamente</strong> il file:
                  </p>
                  <p className="font-mono bg-muted p-2 rounded text-sm">
                    📄 {entryToDelete?.name}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Il file verrà eliminato permanentemente dal tuo disco. 
                    Questa operazione non può essere annullata.
                  </p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* File Preview Modal */}
      {previewEntry && previewBlob && (
        <FilePreview
          entry={previewEntry}
          blob={previewBlob}
          onClose={() => {
            setPreviewEntry(null);
            setPreviewBlob(null);
          }}
        />
      )}

      {/* Path Input Dialog (per inserire il percorso completo) */}
      <Dialog open={showPathInputDialog} onOpenChange={setShowPathInputDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configura cartella documenti</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Inserisci il percorso UNC completo della cartella pratica (allineato al percorso NAS in Impostazioni
              {documentsNasRoot ? `: ${documentsNasRoot}` : ''}).
            </p>
            <div className="space-y-2">
              <label className="text-sm font-medium">Percorso cartella:</label>
              <Input
                placeholder="Es: Y:\Area ICT\Clienti\NomePratica"
                value={pathInputValue}
                onChange={(e) => setPathInputValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleConfirmDirectorySelection()}
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                Copia il percorso da Esplora File (barra degli indirizzi) per essere sicuro
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPathInputDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleConfirmDirectorySelection} disabled={!pathInputValue.trim()}>
              Seleziona Cartella
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File Info Dialog (per file non previewable) */}
      <Dialog open={showFileInfoDialog} onOpenChange={setShowFileInfoDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              Informazioni file
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted p-3 rounded space-y-2">
              <p className="font-medium">📄 File:</p>
              <p className="font-mono text-sm break-all">{fileInfoEntry?.name}</p>
              <p className="font-medium mt-3">📁 Percorso completo:</p>
              <p className="font-mono text-sm break-all bg-white p-2 rounded border select-all">
                {fileInfoEntry ? getFullPath(fileInfoEntry) : ''}
              </p>
            </div>
            
            {inElectron ? (
              <div className="bg-green-50 border border-green-200 rounded p-3 text-green-800">
                <p className="text-sm">
                  Fai doppio click sul file o clicca &quot;Apri con programma&quot; per aprirlo con l&apos;applicazione predefinita.
                </p>
              </div>
            ) : (
              <>
                <div className="bg-green-50 border border-green-200 rounded p-3 text-green-800">
                  <p className="text-sm">
                    Fai doppio click sul file o clicca &quot;Apri con programma&quot; per aprirlo direttamente.
                  </p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-yellow-800">
                  <p className="text-sm">
                    Installa il <strong>client desktop Studio Gori</strong> (MSI dalla pagina Download) per aprire i file direttamente.
                  </p>
                  <a 
                    href="/downloads/" 
                    target="_blank"
                    className="inline-block mt-2 text-sm bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-700 transition-colors"
                  >
                    Scarica e installa
                  </a>
                </div>
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                if (fileInfoEntry) {
                  navigator.clipboard.writeText(getFullPath(fileInfoEntry));
                  toast.success('Percorso copiato negli appunti');
                }
              }}
            >
              Copia percorso
            </Button>
            <Button onClick={() => setShowFileInfoDialog(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default FileExplorer;
